import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { clearWorkerOwner, mustChangePassword, refreshSession, resumeQueue } from '../../lib/auth'
import { drainQueue } from '../../lib/idb-queue'
import { t, type Lang } from '../../lib/i18n'
import { PasswordInput } from '../../components/PasswordInput'

interface Props {
  lang: Lang
  onLangToggle: () => void
  isOnline: boolean
}

/**
 * Distinguishes a locked-account refusal from a plain credential rejection.
 * Parses defensively: an absent or unparseable body must never throw a second
 * error into the operator's face — it just falls back to the generic case.
 */
async function isLockedResponse(res: Response): Promise<{ locked: boolean; retryAfterSeconds?: number }> {
  let bodyCode: string | undefined
  let retryAfterSeconds: number | undefined
  try {
    const body = (await res.json()) as { error?: { code?: string; retryAfterSeconds?: number } }
    bodyCode = body?.error?.code
    retryAfterSeconds = body?.error?.retryAfterSeconds
  } catch {
    // absent/unparseable body — fall back to status alone
  }
  return { locked: res.status === 429 || bodyCode === 'ACCOUNT_LOCKED', retryAfterSeconds }
}

// Renders the SERVER's number, never a client-side countdown — each fresh attempt returns
// a fresh, smaller retryAfterSeconds. Degrades to the lang's windowless lock copy rather
// than showing NaN/undefined when the field is absent.
function lockedMessage(lang: Lang, retryAfterSeconds?: number): string {
  if (typeof retryAfterSeconds === 'number' && Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0) {
    const minutes = Math.max(1, Math.round(retryAfterSeconds / 60))
    return t(lang, 'loginLocked').replace('{minutes}', String(minutes))
  }
  return t(lang, 'loginLockedNoWindow')
}

export function LoginScreen({ lang, onLangToggle, isOnline }: Props) {
  const navigate = useNavigate()
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [keepSignedIn, setKeepSignedIn] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      // R413 (c), Frank #62950: no worker owner while the cookie may be changing hands.
      await clearWorkerOwner()
      const res = await fetch('/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(keepSignedIn ? { username, password, trustedDevice: true } : { username, password }),
      })
      if (res.ok) {
        // Gateway sets the httpOnly ops_session cookie itself; no token ever reaches JS.
        await refreshSession()
        // R413 (c): a person whose password the owner set chooses their own first; their
        // queue stays paused (every write would 403). Anyone else resumes the queue the way
        // App.tsx's re-auth overlay does, so a queue paused by a must-change 403 or an
        // earlier 401 drains without a reload.
        if (mustChangePassword()) {
          navigate('/change-password', { replace: true })
        } else {
          resumeQueue()
          drainQueue().catch(() => {})
          navigate('/cockpit', { replace: true })
        }
      } else {
        const { locked, retryAfterSeconds } = await isLockedResponse(res)
        setError(locked ? lockedMessage(lang, retryAfterSeconds) : t(lang, 'loginFailed'))
      }
    } catch {
      // A network failure (offline, DNS, connection refused) never reached the server at
      // all — a distinct event from a server-issued rejection, so it reads differently.
      setError(t(lang, 'loginNetworkError'))
    } finally {
      setSubmitting(false)
    }
  }

  const dir = lang === 'ar' ? 'rtl' : 'ltr'

  return (
    <div dir={dir} className="min-h-screen bg-background font-sans">
      <header className="bg-primary text-primary-foreground px-4 py-3 flex items-center justify-between">
        <h1 className="text-xl font-semibold tracking-wide">{t(lang, 'appTitle')}</h1>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5">
            <span
              className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${isOnline ? 'bg-green-400' : 'bg-red-400'}`}
            />
            <span className="text-primary-foreground/80 text-xs hidden sm:inline">
              {t(lang, isOnline ? 'online' : 'offline')}
            </span>
          </div>
          <button
            onClick={onLangToggle}
            className="border border-primary-foreground/40 bg-transparent hover:bg-primary-foreground/10 text-primary-foreground text-sm font-semibold px-3 py-1.5 rounded-md min-h-[44px] min-w-[64px] transition-colors"
          >
            {t(lang, 'langToggle')}
          </button>
        </div>
      </header>

      <main className="flex items-center justify-center min-h-[calc(100vh-56px)] px-4">
        <div className="bg-card rounded-xl border border-border shadow-sm p-8 w-full max-w-sm">
          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div id="login-error" className="rounded-md bg-destructive/10 border border-destructive/30 px-4 py-3 text-sm text-destructive">
                {error}
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                {t(lang, 'username')}
              </label>
              <input
                id="login-username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                autoComplete="username"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                className="w-full rounded-md border border-input px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                {t(lang, 'password')}
              </label>
              <PasswordInput
                lang={lang}
                id="login-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                className="w-full rounded-md border border-input px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
              />
            </div>
            <label className="flex items-center gap-2 text-sm text-foreground min-h-[44px]">
              <input
                id="login-keep-signed-in"
                type="checkbox"
                checked={keepSignedIn}
                onChange={(e) => setKeepSignedIn(e.target.checked)}
                className="h-5 w-5 rounded border-input"
              />
              <span>{t(lang, 'keepSignedIn')}</span>
            </label>
            <button
              id="login-submit"
              type="submit"
              disabled={submitting}
              className="w-full rounded-md bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50 transition-colors min-h-[44px]"
            >
              {submitting ? t(lang, 'signingIn') : t(lang, 'signIn')}
            </button>
          </form>
        </div>
      </main>
    </div>
  )
}
